import Vue from 'vue';
import values from 'lodash/values';
import filter from 'lodash/filter';
import {
    fetchTrackTypes,
    fetchTrackType,
    updateTrackType,
    createTrackType,
    deleteTrackType,
} from './track-types.utils';
import { createFrozenMap } from '@/vuex/utils';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';

let loadAllPromise;

const trackTypes = {
    namespaced: true,
    state: {
        error: {},
        fetching: false,
        map: {},
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, trackType }) {
            Vue.set(state.map, id, trackType);
        },
        deleteMapKey (state, { id }) {
            Vue.delete(state.map, id);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
    },
    actions: {
        async loadAll ({ commit }, { reFetch = false } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            commit('setFetching', { fetching: true });
            loadAllPromise = fetchTrackTypes();

            try {
                const { data: trackTypes } = await loadAllPromise;
                commit('setFetching', { fetching: false });
                commit('setMap', {
                    map: createFrozenMap(trackTypes),
                });
            } catch (error) {
                loadAllPromise = null;
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        loadOne ({ commit, getters }, { reFetch = false, ...payload }) {
            const { id } = payload;
            if (!reFetch && getters.trackTypeById(id)) return Promise.resolve();

            commit('setFetching', { fetching: true });

            return fetchTrackType(id)
                .then(({ data: trackType }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: trackType.id,
                        trackType: Object.freeze(trackType),
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        create ({ commit }, payload = {}) {
            commit('setFetching', { fetching: true });

            return createTrackType(payload)
                .then(({ data: trackType }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: trackType.id,
                        trackType: Object.freeze(trackType),
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        update ({ commit }, { shouldStartProcessing = false, ...payload } = {}) {
            commit('setFetching', { fetching: true });

            return updateTrackType(payload, shouldStartProcessing)
                .then(({ data: trackType }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: trackType.id,
                        trackType: Object.freeze(trackType),
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        remove ({ commit }, { id }) {
            commit('setFetching', { fetching: true });

            return deleteTrackType(id)
                .then(() => {
                    commit('setFetching', { fetching: false });
                    commit('deleteMapKey', { id });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
    },
    getters: {
        list: (state) => values(state.map),
        trackTypeById: (state) => (id) => state.map[id],
        trackTypesByAppId: (state, getters) => (appId) =>
            filter(getters.list, (trackType) => trackType.appId === appId),
        analyticsTrackTypesListWithStars: (state, getters, _rootState, rootGetters) => {
            const isStarred = rootGetters['stars/getStarByItemId'];
            if (state.fetching) return null;

            return getters.list.map((trackType) =>
                Object.freeze({
                    id: trackType.id,
                    name: trackType.name,
                    group: trackType.group,
                    appId: trackType.appId,
                    appIcon: rootGetters['apps/getIconByAppId'](trackType.appId),
                    kind: trackType.kind,
                    dirty: trackType.dirty,
                    isCoreEvent: trackType.isCoreEvent,
                    isStarred: !!isStarred(trackType.id),
                    icon: trackType.icon || { type: 'zap' },
                }),
            );
        },
        recentTrackTypesInReporting: (state, _getters, _rootState, rootGetters) => {
            const isStarred = rootGetters['stars/getStarByItemId'];

            return rootGetters['userSettings/history/itemHistory'](
                RECENTLY_SELECTED_HISTORY_TYPES.TRACKTYPE_RECENTLY_SELECTED,
            )
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => {
                    const trackType = state.map[id];
                    const app = rootGetters['apps/getAppById'](trackType.appId);

                    return {
                        id: trackType.id,
                        name: trackType.name,
                        group: trackType.group,
                        appId: trackType.appId,
                        appIcon: rootGetters['apps/getIconByAppId'](trackType.appId),
                        appDisplayName: app ? app.displayName : undefined,
                        kind: trackType.kind,
                        dirty: trackType.dirty,
                        isCoreEvent: trackType.isCoreEvent,
                        isStarred: !!isStarred(trackType.id),
                        icon: trackType.icon || { type: 'zap' },
                        lastUsed: timestamp,
                    };
                });
        },
    },
};

export default trackTypes;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}
